"use client";

import { useEffect, useState } from "react";
import { StrategyAboutContent } from "@/app/components/StrategyAboutContent";
import {
  bastionButtonHover,
  bastionButtonIconColor,
  bastionButtonStyle,
} from "@/lib/bastion-button";
import { getStrategyIconForCode } from "@/lib/strategy-icons";
import type { MethodologyStrategy } from "@/lib/methodology/strategies";
import "./StrategyExplorer.css";

const FALLBACK_COLOR = "#DCE2EC";

/** The page-wide lifted card surface (defined on the article in methodology.module.css). */
const CARD_SURFACE = {
  background: "var(--method-card-bg)",
  border: "1px solid var(--method-card-border)",
  boxShadow: "var(--method-card-shadow)",
};

function useIsMobile(maxWidth = 760) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [maxWidth]);
  return isMobile;
}

export default function StrategyExplorer({
  strategies,
  label,
  showSteps = false,
}: {
  strategies: MethodologyStrategy[];
  label: string;
  showSteps?: boolean;
}) {
  const [activeCode, setActiveCode] = useState(strategies[0]?.strategycode ?? "");
  const isMobile = useIsMobile();

  if (strategies.length === 0) return null;
  const active = strategies.find((s) => s.strategycode === activeCode) ?? strategies[0];
  const activeName = active.public_name || active.strategyname || "";

  return (
    <section className="sx" aria-label={label}>
      <div className="sx-tabs" role="tablist">
        {strategies.map((strategy) => {
          const selected = strategy.strategycode === active.strategycode;
          const color = strategy.color || FALLBACK_COLOR;
          const Icon = getStrategyIconForCode(strategy.strategycode, strategy.icon);
          return (
            <button
              key={strategy.strategycode}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActiveCode(strategy.strategycode)}
              style={bastionButtonStyle({ color, isSelected: selected, height: 34, paddingX: 14 })}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = bastionButtonHover(color, selected, "enter");
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = bastionButtonHover(color, selected, "leave");
              }}
            >
              {Icon ? (
                <Icon size={14} strokeWidth={2.2} color={bastionButtonIconColor(color, selected)} />
              ) : null}
              {strategy.public_name || strategy.strategyname}
            </button>
          );
        })}
      </div>

      <div className="sx-panel" role="tabpanel" aria-label={activeName}>
        <header className="sx-head">
          <span className="sx-dot" style={{ background: active.color || FALLBACK_COLOR }} />
          <h3 className="sx-title">{activeName}</h3>
          {active.tagline ? <span className="sx-tagline">{active.tagline}</span> : null}
        </header>
        <StrategyAboutContent
          strategy={active}
          isMobile={isMobile}
          layout={isMobile ? "stacked" : "two-column"}
          showSteps={showSteps}
          methodologySurface={CARD_SURFACE}
        />
      </div>
    </section>
  );
}
